/**
 * Refresh Stage 11: SEO Validation
 *
 * Reuses creation Stage 6 logic directly. Pure code — runs 14-check SEO
 * validation suite on the assembled article. Same UI as Stage6SeoV2.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const validation = (doc.preflight as any)?.validation;
  if (!validation) {
    return <div className="text-sm text-gray-500">No validation results yet.</div>;
  }

  const report = validation.validation_report || validation;
  const checks = Array.isArray(report.checks) ? report.checks : [];
  const overallStatus = report.overall_status || validation.overall_status;

  if (checks.length === 0) {
    return <div className="text-sm text-gray-500">No checks found in validation report.</div>;
  }

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border p-4 ${
        overallStatus === 'pass' ? 'bg-green-50 border-green-200'
        : overallStatus === 'warn' ? 'bg-amber-50 border-amber-200'
        : 'bg-red-50 border-red-200'
      }`}>
        {overallStatus === 'pass' && (
          <p className="text-sm font-medium text-green-800">All checks passed. Article passed SEO validation.</p>
        )}
        {overallStatus === 'warn' && (
          <p className="text-sm font-medium text-amber-800">Warnings present. Review flagged items before proceeding.</p>
        )}
        {overallStatus === 'fail' && (
          <p className="text-sm font-medium text-red-800">Failures detected. Fix SEO issues below, then re-run or proceed.</p>
        )}
      </div>

      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="text-left py-2 px-3 w-8">&mdash;</th>
              <th className="text-left py-2 px-3">Check</th>
              <th className="text-left py-2 px-3">Measured value</th>
              <th className="text-left py-2 px-3">Fix (if any)</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((c: any) => (
              <tr key={c.check_id} className="border-b border-gray-100">
                <td className="py-2 px-3">
                  {c.status === 'pass' ? '✅' : c.status === 'warn' ? '⚠️' : '❌'}
                </td>
                <td className="py-2 px-3 font-medium">{c.check_name}</td>
                <td className="py-2 px-3 text-gray-700">
                  {typeof c.measured_value === 'object' ? JSON.stringify(c.measured_value) : String(c.measured_value || '—')}
                </td>
                <td className="py-2 px-3 text-gray-600">{c.fix ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <details className="text-xs text-gray-600">
        <summary className="cursor-pointer font-medium">Expand details per check</summary>
        <div className="mt-2 space-y-2">
          {checks.map((c: any) => (
            <details key={c.check_id} className="border rounded p-2 bg-gray-50">
              <summary className="cursor-pointer">
                {c.status === 'pass' ? '✅' : c.status === 'warn' ? '⚠️' : '❌'} {c.check_name}
              </summary>
              {c.details != null && (
                <pre className="mt-1 text-xs whitespace-pre-wrap overflow-x-auto">
                  {typeof c.details === 'object' ? JSON.stringify(c.details, null, 2) : String(c.details)}
                </pre>
              )}
            </details>
          ))}
        </div>
      </details>
    </div>
  );
}

export function RefreshSeoV2() {
  const { document } = useArticleContext();
  const hasMarkdown = Boolean(document?.article?.markdown);

  return (
    <StagePanel
      stageId="refresh_11_seo"
      title="Refresh: SEO Validation"
      description="Runs code-based SEO checks on the assembled article."
      runDisabled={!hasMarkdown}
      runDisabledReason={!hasMarkdown ? 'Requires article markdown' : undefined}
      renderOutput={renderOutput}
    >
      {document?.article?.seo && (
        <div className="text-xs text-gray-600 bg-gray-50 rounded border p-2 space-y-1">
          <div><span className="text-gray-400">Title:</span> {document.article.seo.meta_title}</div>
          <div><span className="text-gray-400">Slug:</span> /{document.article.seo.slug}</div>
          <div><span className="text-gray-400">Primary keyword:</span> {document.targeting?.keywords?.primary?.keyword || '—'}</div>
        </div>
      )}
    </StagePanel>
  );
}
